/**
 * Display - read-only 3-line glance readout (DisplayItem). One item = one
 * bound source, picked in the Inspector. NEVER sends.
 *
 * Face markup is class-for-class the same as design.html's `.display-readout`
 * mockup (`dr-line` / `dr-sub`) - what makes `skin_matches_workbench`
 * meaningful for this component. `data-style` picks skin-matched (default,
 * pure --sk-* tokens) vs the fixed LCD look (deliberate exception, see
 * skin-components.css).
 *
 * LFO state reads live from `lfo/runtime.ts` (`lfoState(boundId)`) rather
 * than a local subscription, so a re-bind of `lfoId` in the Inspector keeps
 * working without re-mounting - each render reads `props.item.lfoId` fresh.
 *
 * Two known v1 limitations - see DisplayItem's doc comment in
 * projectStore.ts for the full framing:
 * - `source: 'helix'` shows the LAST command OPButtons sent, not live pedal
 *   state (no MIDI-in feedback path exists).
 * - `source: 'timeline'` binds to one specific TimelineLauncherItem; a
 *   missing binding renders as a loud dead state, not a stale value.
 *
 * `source: 'nowplaying'` (DEC-315) is the one source with its own face
 * (`DisplayNowPlaying.tsx`): a strip of the playing song timeline and a
 * countdown, laid out by the item's size, inside the same readout box.
 */

import { Show, createMemo, createEffect, onCleanup, onMount, type Accessor } from 'solid-js';
import { InstrumentIcon } from './InstrumentIcon.tsx';
import { DISPLAY_KIND, displayL1Max } from '../layout/displayLook.ts';
import { HUE_VALUES } from '../layout/projectStore.ts';
import { sectionHue } from '../timeline/sectionHue.ts';
import { NowPlayingFace } from './DisplayNowPlaying.tsx';
import { lfoState } from '../lfo/runtime.ts';
import { layoutItems, type DisplayItem, type LayoutColor } from '../layout/store.ts';
import { activePedals } from '../layout/projectStore.ts';
import { clockSource, clockSourceLabel } from '../opbridge/clockPoll.ts';
import { timelineRuntimeStates } from '../timeline/runtime.ts';
import { buttonRuntimeStates } from '../layout/buttonRuntime.ts';
import { timelines } from '../timeline/store.ts';
import { helixLastCommanded } from '../helix/lastCommanded.ts';
import { helixPresetName, helixSetlistName } from '../helix/library.ts';
import { helixSlotLabel } from './PresetChange.tsx';
import { snapshotLabel } from './SnapshotChange.tsx';
import { dropTuning, capoForTranspose } from '../transpose/session.ts';
import { noteName } from '../autotune/session.ts';
import { setlistState, observedTranspose, songAutotune, songAutotuneStatus, songAutotuneWhy, type VocalsKeyMode } from '../setlist/state.ts';
import { abletonState } from '../opbridge/clockPoll.ts';
import { displayRecQuant, displayLaunchQuant } from '@opbuttons/schema';

interface DisplayProps {
  item: DisplayItem;
  /** Live tempo - threaded from App.tsx's smoothClock instance, same as
   *  Header's own `bpm` prop. Only read when `source === 'bpm'`. */
  bpm?: Accessor<number>;
  /** Whether Live's transport currently owns the clock - same prop Header
   *  reads for its `2-decimal + "live"` vs `whole-number + "bpm"` face
   *  (feedback_slave_state_visual_convention). Read by `bpm`, `timesig`
   *  and `transport`. */
  isSlaved?: Accessor<boolean>;
  /** Time signature of the ACTIVE clock - the live one when slaved, the
   *  practice one otherwise. Threaded from App so this reads the same truth
   *  as `beatsPerBar` and the Header, rather than deriving a second answer
   *  that could disagree with them. Only read when `source === 'timesig'`. */
  sig?: Accessor<[number, number]>;
  /** Whether the ACTIVE clock is running. Only read when
   *  `source === 'transport'`. */
  isPlaying?: Accessor<boolean>;
  /** The song beat, for the beat dots (DEC-319). Only read while the dots show and the clock runs. */
  beat?: Accessor<number>;
}

const COLOR_TOKEN: Record<LayoutColor, string> = {
  cyan: 'idle',
  magenta: 'incoming',
  amber: 'armed',
  green: 'ok',
};

/** `song` is a timeline's name, on its own line (DEC-319); `idle` marks a line that says nothing is there yet, which
 *  is drawn quieter than a reading. */
interface Lines { l1: string; l2: string; l3: string; song?: string; idle?: boolean }

/** "+7" / "0" / "-5" - same explicit-sign convention as Transpose.tsx's own
 *  `semitoneLabel` (kept local here rather than exported/shared, since it's
 *  a one-liner). */
function signed(n: number): string {
  return n > 0 ? `+${n}` : String(n);
}

/** Spelled out here, unlike the item's five-cell row which has to fit an
 *  iPad: this is a readout, so it can afford the unambiguous word. "MIN"
 *  alone would be a lie now that there are two minors. DORIAN is the one
 *  entry whose short form is already unambiguous, so it does not gain
 *  anything from the long form and keeps the same word. */
/** The three modes OPBridge's key parser can report, which is COARSER than
 *  the five-way scale row on a Waves tile - the setlist frame carries a key,
 *  not a scale.
 *
 *  This REPLACED a `SCALE_LABEL` map of the tile's own five scales, which
 *  became dead the moment `keyLines` started reading the authority. Worth a
 *  line because `pnpm typecheck` did NOT flag it: a module-level const with
 *  no readers still passes, so dead code of this shape has to be found by
 *  grepping its own name rather than by the compiler. */
const MODE_LABEL: Record<VocalsKeyMode, string> = {
  major: 'MAJOR', minor: 'MINOR', chromatic: 'CHROMATIC',
};

export function Display(props: DisplayProps) {
  function bpmLines(): Lines {
    const bpm = props.bpm?.() ?? 0;
    const slaved = props.isSlaved?.() ?? false;
    return {
      l1: slaved ? bpm.toFixed(2) : bpm.toFixed(0),
      l2: slaved ? 'BPM · live' : 'BPM',
      l3: clockSourceLabel(clockSource()),
    };
  }

  // ─── Ableton Control readouts ────────────────────────────────
  // Two different sources on purpose. `timesig`/`transport` describe the
  // ACTIVE clock, so they follow the same live-vs-practice rule `bpm` does
  // and stay meaningful with no laptop. The quant/metronome trio are
  // Ableton-session properties with no local equivalent, so they read the
  // device observations and honestly show `--` when Ableton is absent.

  function timeSigLines(): Lines {
    const [n, d] = props.sig?.() ?? [0, 0];
    const slaved = props.isSlaved?.() ?? false;
    if (n <= 0 || d <= 0) return { l1: '--', l2: 'TIME SIG', l3: '' };
    return {
      l1: `${n}/${d}`,
      l2: 'TIME SIG',
      l3: slaved ? clockSourceLabel(clockSource()) : 'practice',
    };
  }

  function transportLines(): Lines {
    const playing = props.isPlaying?.() ?? false;
    const slaved = props.isSlaved?.() ?? false;
    return {
      l1: playing ? 'PLAY' : 'STOP',
      l2: 'TRANSPORT',
      l3: slaved ? clockSourceLabel(clockSource()) : 'practice',
    };
  }

  /** Seconds-ago suffix for a device observation, or '' when we have never
   *  heard one. Deliberately coarse - this is a "is this current?" cue, not
   *  a stopwatch. */
  function observedAge(atUnixMs: number | undefined): string {
    if (!atUnixMs) return '';
    const secs = Math.max(0, Math.round((Date.now() - atUnixMs) / 1000));
    if (secs < 2) return 'now';
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
    return `${Math.round(secs / 3600)}h ago`;
  }

  function recQuantLines(): Lines {
    const obs = abletonState().recQuant;
    // displayRecQuant renders absent as '--', never 'None' - "0" is a REAL
    // label meaning None, and showing it when we simply have not heard from
    // the device would be a wrong answer dressed as a real one.
    return { l1: displayRecQuant(obs?.label), l2: 'REC QUANT', l3: observedAge(obs?.at_unix_ms) };
  }

  function launchQuantLines(): Lines {
    const obs = abletonState().launchQuant;
    return { l1: displayLaunchQuant(obs?.label), l2: 'LAUNCH QUANT', l3: observedAge(obs?.at_unix_ms) };
  }

  function metronomeLines(): Lines {
    const obs = abletonState().metronome;
    // Live's AUDIBLE click, not our beat dots. Absent stays '--' rather than
    // defaulting to OFF, which would look identical to a real "click is off".
    return {
      l1: obs === null ? '--' : obs.on ? 'ON' : 'OFF',
      l2: 'METRONOME',
      l3: observedAge(obs?.at_unix_ms),
    };
  }

  function lfoLines(): Lines {
    const boundId = props.item.lfoId;
    if (!boundId) return { l1: 'No LFO', l2: 'pick one in the Inspector', l3: '', idle: true };
    const lfo = layoutItems().find((i) => i.id === boundId && i.kind === 'lfo');
    if (!lfo || lfo.kind !== 'lfo') return { l1: 'LFO removed', l2: 'pick another in the Inspector', l3: '', idle: true };
    const name = lfo.name || lfo.waveform;
    const s = lfoState(boundId);
    return { l1: name, l2: s.state, l3: `${Math.round(s.currentValue)}` };
  }

  function timelineLines(): Lines {
    const boundId = props.item.timelineLauncherId;
    if (!boundId) return { l1: 'No timeline', l2: 'pick a launcher in the Inspector', l3: '', idle: true };
    const launcherItem = layoutItems().find((i) => i.id === boundId && i.kind === 'timelineLauncher');
    if (!launcherItem || launcherItem.kind !== 'timelineLauncher') {
      return { l1: 'Launcher removed', l2: 'pick another in the Inspector', l3: '', idle: true };
    }
    const runtime = timelineRuntimeStates()[boundId];
    const t = timelines().find((tl) => tl.id === launcherItem.timelineId);
    const name = t?.name ?? 'timeline';
    if (!runtime || (!runtime.playing && !runtime.armed)) return { song: name, l1: 'Not launched', l2: 'tap a section to start', l3: '', idle: true };
    const section = runtime.activeSectionId
      ? t?.sections.find((s) => s.id === runtime.activeSectionId)?.name ?? runtime.activeSectionId
      : '';
    return { song: name, l1: section || 'Cued', l2: `bar ${runtime.barReadout}`, l3: `${Math.round(runtime.playheadPct)}% through` };
  }

  function helixLines(): Lines {
    const pedalId = props.item.pedalId;
    if (!pedalId) return { l1: 'No pedal', l2: 'pick one in the Inspector', l3: '', idle: true };
    const pedal = activePedals().find((p) => p.id === pedalId);
    if (!pedal) return { l1: 'Pedal removed', l2: 'pick another in the Inspector', l3: '', idle: true };
    const cmd = helixLastCommanded()[pedalId];
    if (!cmd) return { l1: 'Nothing sent yet', l2: 'press a preset to send it', l3: '', idle: true };
    const ago = Math.max(0, Date.now() - cmd.at);
    const agoText = ago < 60_000 ? `${Math.round(ago / 1000)}s ago` : `${Math.round(ago / 60_000)}m ago`;
    if (cmd.kind === 'preset') {
      // The latest read leads, as on the preset tile; the name recorded
      // with the send is the fallback.
      const known = typeof cmd.setlist === 'number' && typeof cmd.program === 'number';
      const name = (known ? helixPresetName(cmd.setlist!, cmd.program!) : undefined)
        ?? cmd.presetName
        ?? (known ? helixSlotLabel(cmd.program!) : '?');
      const setlist = typeof cmd.setlist === 'number' ? helixSetlistName(cmd.setlist) : '';
      // Ride-along snapshot (DEC-151) - only present when the fire also set
      // one, so a plain preset recall doesn't claim a snapshot it never sent.
      const snap = cmd.snapshot !== undefined ? ` · ${snapshotLabel(cmd.snapshot)}` : '';
      return { l1: name, l2: `${setlist}${snap} · sent`, l3: agoText };
    }
    return { l1: pedal.name, l2: `${snapshotLabel(cmd.snapshot ?? 0)} · sent`, l3: agoText };
  }

  /**
   * ⚠️ THESE THREE READ THE AUTHORITY, NOT THE LATCH, and that is the whole
   * point of them.
   *
   * All three used to read `concertTranspose()` / `autotuneKey()` - local
   * `localStorage` signals that only a tap or a Rig State recall ever writes.
   * A Display bound to any of them therefore showed WHAT YOU LAST SENT while
   * claiming to report the rig, and an OPSetlist song change could not move
   * it. That is the same defect the Transpose row had before `cf2b6b2` and
   * the Capo row had until 2026-09-21; this was the THIRD instance, and it
   * was found by enumerating every reader of the latch rather than by anyone
   * noticing it - which is the only reason it did not become a fourth
   * operator report.
   *
   * `--` for absent, never a default - the rule this whole arc turns on.
   * Zero is a real transpose, fret 0 is a real capo position, and a defaulted
   * value is indistinguishable from "we have not been told".
   */
  function setlistAge(): string {
    return observedAge(setlistState().lastSent?.at_unix_ms ?? undefined);
  }

  /** `drop -1` alone when there is no reading yet, `drop -1 · 4s ago` when
   *  there is. The drop tuning is a SETTING and always known; the age belongs
   *  to the readback and may not exist. */
  function dropAndAge(): string {
    const age = setlistAge();
    return age ? `drop ${dropTuning()} · ${age}` : `drop ${dropTuning()}`;
  }

  function transposeLines(): Lines {
    const t = observedTranspose(setlistState());
    return { l1: t === null ? '--' : signed(t), l2: 'TRANSPOSE', l3: dropAndAge() };
  }

  function capoLines(): Lines {
    const t = observedTranspose(setlistState());
    return {
      l1: t === null ? '--' : signed(capoForTranspose(t)),
      l2: 'CAPO',
      l3: dropAndAge(),
    };
  }

  /**
   * The autotune key the last SENT song asked for.
   *
   * Read with `songAutotune` - the SAME function the Waves tile uses, and
   * deliberately not a second derivation: two readers of one value drifting
   * apart is exactly what produced the capo bug. It is OPBridge's own parse
   * of the key, and only when its CCs actually left, so a key it rejected or
   * could not send is a key the plugin was never told about, and `--` is
   * then the truthful answer.
   *
   * Reads the APPLIED song (the last one actually sent; `setlist/state.ts::
   * appliedSong`), not `current`: with auto-sync off, scrolling OPSetlist moves
   * `current` and sends nothing. `last_sent` cannot stand in, since it
   * deliberately carries no `vocals_key` (Waves state is not queryable).
   *
   * The MODE comes from the authority too. A Waves tile's scale row lights
   * from the same song mode, but shows a tap newer than the song as pending
   * (`autotune/readback.ts`); this line always reports what the SONG asked.
   */
  function keyLines(): Lines {
    const parsed = songAutotune(setlistState());
    const age = setlistAge();
    if (parsed === null) {
      // Say WHY when the song gave a definite reason (same line the Waves
      // tile shows), rather than a bare -- that reads like "not read yet".
      const why = songAutotuneWhy(songAutotuneStatus(setlistState()));
      return why ? { l1: '--', l2: 'NO KEY SENT', l3: why } : { l1: '--', l2: 'AUTOTUNE', l3: age };
    }
    return {
      l1: noteName(parsed.pitchClass),
      l2: MODE_LABEL[parsed.mode],
      l3: age ? `AUTOTUNE · ${age}` : 'AUTOTUNE',
    };
  }

  function buttonLines(): Lines {
    const boundId = props.item.buttonId;
    if (!boundId) return { l1: 'No button', l2: 'pick one in the Inspector', l3: '', idle: true };
    const btn = layoutItems().find((i) => i.id === boundId && i.kind === 'button');
    if (!btn || btn.kind !== 'button') return { l1: 'Button removed', l2: 'pick another in the Inspector', l3: '', idle: true };
    const name = btn.name || (
      btn.actionType === 'noteon' ? `note ${btn.note ?? 60}`
        : btn.actionType === 'pc' ? `pc ${btn.program ?? 0}`
          : `cc ${btn.ccNumber}`
    );
    const on = buttonRuntimeStates()[boundId] ?? false;
    return { l1: name, l2: on ? 'ON' : 'OFF', l3: '' };
  }

  const lines = createMemo<Lines>(() => {
    const r = rawLines();
    return r.idle === undefined && r.l1 === '--' ? { ...r, idle: true } : r;
  });
  function rawLines(): Lines {
    switch (props.item.source) {
      case 'bpm': return bpmLines();
      case 'timesig': return timeSigLines();
      case 'transport': return transportLines();
      case 'recquant': return recQuantLines();
      case 'launchquant': return launchQuantLines();
      case 'metronome': return metronomeLines();
      case 'lfo': return lfoLines();
      case 'timeline': return timelineLines();
      case 'helix': return helixLines();
      case 'button': return buttonLines();
      case 'transpose': return transposeLines();
      case 'capo': return capoLines();
      case 'key': return keyLines();
      // Drawn by its own face below, never as three lines.
      case 'nowplaying': return { l1: '', l2: '', l3: '' };
    }
  }

  // ---- The screen (DEC-319) ----
  const kind = () => DISPLAY_KIND[props.item.source];
  const labelAt = () => props.item.labelAt ?? 'tl';
  /** What the label says after the kind: the thing it reads, by name where there is one. */
  const detail = (): string => {
    const it = props.item;
    if (it.source === 'helix') return activePedals().find((p) => p.id === it.pedalId)?.name ?? kind().source;
    if (it.source === 'button') { const b = layoutItems().find((i) => i.id === it.buttonId); return (b && 'name' in b && b.name) || kind().source; }
    if (it.source === 'lfo') { const l = layoutItems().find((i) => i.id === it.lfoId); return (l && 'name' in l && l.name) || kind().source; }
    return kind().source;
  };
  const extra = () => (props.item.source === 'nowplaying' && (props.isPlaying?.() ?? false) ? `${Math.round(props.bpm?.() ?? 0)} BPM` : '');
  /** The ink (--dc): the item's own colour, else what it shows (Auto): a section's colour, the kind's, or the skin's. */
  const ink = (): string => {
    const style = props.item.style ?? 'skin';
    if (style === 'lcd') return '#7dff9a';
    const h = props.item.hue;
    if (h && h !== 'none') return HUE_VALUES[h];
    switch (props.item.source) {
      case 'timeline': return lines().idle ? '#5aa2ff' : sectionHue(lines().l1);
      case 'nowplaying': return '#5aa2ff';
      case 'helix': return '#ff9a3d';
      case 'transpose': return HUE_VALUES.white;
      case 'capo': return HUE_VALUES.yellow;
      case 'key': return HUE_VALUES.cyan;
      case 'recquant': return HUE_VALUES.blue;
      case 'button': return HUE_VALUES.red;
      default: return style === 'glass' ? '#ffffff' : 'var(--sk-ink)';
    }
  };
  const dotsOn = () => !!props.item.showDots && props.item.source !== 'nowplaying';
  const dot = () => (dotsOn() && (props.isPlaying?.() ?? false) && props.beat ? Math.floor(((props.beat() % 4) + 4) % 4) : -1);

  // The big line: as big as fits (never past the Font's share of the height), then centred between what is above it
  // (the timeline's name, else the screen's top) and what is below it (the detail line, the note, else the foot), with
  // the beat dots on the same middle. Measured, so it holds at every size, label position and font.
  let rootEl: HTMLDivElement | undefined;
  let scrEl: HTMLDivElement | undefined;
  let l1El: HTMLSpanElement | undefined;
  let lsEl: HTMLSpanElement | undefined;
  let pipsEl: HTMLSpanElement | undefined;
  let disposed = false;
  onCleanup(() => { disposed = true; });
  function place(): void {
    if (disposed || !scrEl || !l1El || props.item.source === 'nowplaying') return;
    const H = scrEl.clientHeight, W = scrEl.clientWidth - 20 - (pipsEl ? pipsEl.offsetWidth + 8 : 0);
    if (H <= 0) return;
    if (lines().idle) { l1El.style.removeProperty('--l1'); }
    else {
      let fs = displayL1Max(props.item.h, props.item.nameSize);
      l1El.style.setProperty('--l1', `${fs}px`);
      for (let k = 0; k < 60 && l1El.offsetWidth > W && fs > 11; k++) { fs -= 1; l1El.style.setProperty('--l1', `${fs}px`); }
    }
    const vis = (e: Element | null | undefined): e is HTMLElement => !!e && getComputedStyle(e).display !== 'none' && !!e.textContent?.trim();
    const top = vis(lsEl) ? lsEl.offsetTop + lsEl.offsetHeight : 4;
    const below = ['.dr-l2', '.dr-l3'].map((s) => scrEl!.querySelector(s)).find(vis);
    const bot = below ? (below as HTMLElement).offsetTop : H - 4;
    const mid = (top + bot) / 2;
    l1El.style.top = `${Math.round(mid - l1El.offsetHeight / 2)}px`;
    if (pipsEl) pipsEl.style.top = `${Math.round(mid - pipsEl.offsetHeight / 2)}px`;
  }
  createEffect(() => {
    void lines(); void props.item.h; void props.item.nameSize; void labelAt(); void props.item.showDetail; void dotsOn();
    requestAnimationFrame(place);
  });
  let ro: ResizeObserver | undefined;
  onMount(() => { if (rootEl) { ro = new ResizeObserver(() => place()); ro.observe(rootEl); } });
  onCleanup(() => ro?.disconnect());

  return (
    <div
      class="display-readout"
      ref={(el) => (rootEl = el)}
      data-scr=""
      data-style={props.item.style ?? 'skin'}
      data-color={COLOR_TOKEN[props.item.color]}
      data-source={props.item.source === 'nowplaying' ? 'nowplaying' : undefined}
      data-tp={labelAt()}
      data-al={props.item.align ?? 'left'}
      data-sa={props.item.songAlign ?? 'left'}
      data-no2={props.item.showDetail === false ? '' : undefined}
      data-idle={props.item.source !== 'nowplaying' && lines().idle ? '' : undefined}
      style={{
        '--dc': ink(),
        '--cat': kind().colour,
        '--gl': ((props.item.glow ?? 100) / 100).toFixed(2),
      }}
    >
      <Show when={labelAt() !== 'off'}>
        <span class="dr-label">
          <Show when={props.item.labelIcon !== false}><InstrumentIcon id={kind().icon} /></Show>
          <b>{kind().kind}</b>
          <span class="dr-det">{detail()}</span>
          <Show when={extra()}><span class="dr-rt">{extra()}</span></Show>
        </span>
      </Show>
      <div class="dr-scr" ref={(el) => (scrEl = el)}>
        <Show
          when={props.item.source === 'nowplaying'}
          fallback={<>
            <Show when={lines().song}><span class="dr-line dr-ls" ref={(el) => (lsEl = el)}>{lines().song}</span></Show>
            <span class="dr-line dr-l1" ref={(el) => (l1El = el)}>{lines().l1}</span>
            <span class="dr-line dr-sub dr-l2">{lines().l2}</span>
            {lines().l3 && <span class="dr-line dr-sub dr-l3">{lines().l3}</span>}
            <Show when={dotsOn()}>
              <span class="dr-pips" ref={(el) => (pipsEl = el)}>
                <i data-on={dot() === 0 ? '' : undefined} /><i data-on={dot() === 1 ? '' : undefined} />
                <i data-on={dot() === 2 ? '' : undefined} /><i data-on={dot() === 3 ? '' : undefined} />
              </span>
            </Show>
          </>}
        >
          <NowPlayingFace item={props.item} />
        </Show>
      </div>
    </div>
  );
}
